import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Camera, Save, X } from "lucide-react";

interface ProfileData {
  fullName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  bank: string;
  branch: string;
  accountNumber: string;
  accountName: string;
  introduction: string;
  avatar: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData>({
    fullName: "RNET",
    email: "demo@rnet.vn",
    phone: "0936282119",
    alternatePhone: "",
    bank: "",
    branch: "",
    accountNumber: "",
    accountName: "",
    introduction: "",
    avatar: "",
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    // Handle save logic here
    console.log("Saving profile data:", profileData);
    console.log("Avatar file:", avatarFile);
    // You can add API call here
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <PageShell title="Cập nhật thông tin cá nhân">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Cập nhật thông tin cá nhân
          </h1>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-2xl font-bold">
                      {profileData.fullName.charAt(0)}
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors">
                  <Camera className="h-4 w-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800">
                  Ảnh đại diện
                </h3>
                <p className="text-sm text-gray-600">
                  Chọn ảnh đại diện cho tài khoản của bạn
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Họ tên */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Họ tên <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập họ tên..."
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập email..."
                  />
                </div>

                {/* Điện thoại */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Điện thoại <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập số điện thoại..."
                  />
                </div>

                {/* Điện thoại khác */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Điện thoại khác
                  </label>
                  <input
                    type="tel"
                    value={profileData.alternatePhone}
                    onChange={(e) =>
                      handleInputChange("alternatePhone", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập số điện thoại khác..."
                  />
                </div>

                {/* Ngân hàng */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngân hàng
                  </label>
                  <select
                    value={profileData.bank}
                    onChange={(e) => handleInputChange("bank", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                  >
                    <option value="">- Chọn ngân hàng -</option>
                    <option value="vietcombank">Vietcombank</option>
                    <option value="techcombank">Techcombank</option>
                    <option value="bidv">BIDV</option>
                    <option value="vietinbank">VietinBank</option>
                    <option value="agribank">Agribank</option>
                    <option value="mbbank">MBBank</option>
                    <option value="acb">ACB</option>
                    <option value="vpbank">VPBank</option>
                    <option value="sacombank">Sacombank</option>
                    <option value="tpbank">TPBank</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Chi nhánh - Tỉnh thành */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chi nhánh - Tỉnh thành
                  </label>
                  <input
                    type="text"
                    value={profileData.branch}
                    onChange={(e) =>
                      handleInputChange("branch", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập chi nhánh - tỉnh thành..."
                  />
                </div>

                {/* Số tài khoản */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số tài khoản
                  </label>
                  <input
                    type="text"
                    value={profileData.accountNumber}
                    onChange={(e) =>
                      handleInputChange("accountNumber", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập số tài khoản..."
                  />
                </div>

                {/* Tên tài khoản */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên tài khoản
                  </label>
                  <input
                    type="text"
                    value={profileData.accountName}
                    onChange={(e) =>
                      handleInputChange("accountName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent"
                    placeholder="Nhập tên tài khoản..."
                  />
                </div>

                {/* Giới thiệu */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giới thiệu
                  </label>
                  <textarea
                    value={profileData.introduction}
                    onChange={(e) =>
                      handleInputChange("introduction", e.target.value)
                    }
                    rows={6}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0087c1] focus:border-transparent resize-none"
                    placeholder="Nhập giới thiệu về bản thân..."
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Hủy</span>
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Lưu thông tin</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default Profile;
